import { useCallback, useEffect, useMemo } from "react";
import { authentication } from "@microsoft/teams-js";
import { useTeamsUserCredential } from "@microsoft/teamsfx-react";
import { useTranslation } from "react-i18next";
import { useStore } from "./useStore";
import config from "../components/sample/lib/config";

const getMessage = (e, tokenRequest, setIsLoggedIn) => {
  // inside the parent
  if (e.data === "200") {
    setIsLoggedIn(true);
  }
  if (e.data === "401") {
    tokenRequest(true);
  }
  if (e.data === "noToken") {
    setIsLoggedIn(false);
  }
};

export const useToken = () => {
  const { t } = useTranslation();
  const { context } = useTeamsUserCredential({
    initiateLoginEndpoint: config.initiateLoginEndpoint,
    clientId: config.clientId,
  });

  const {
    token,
    setToken,
    oneTimeToken,
    setOneTimeToken,
    subdomain,
    setError,
    setIsLoggedIn,
    isLoggedIn,
    setStatus,
  } = useStore();

  const email = useMemo(() => context?.user?.loginHint || "", [context?.user?.loginHint]);

  const request = useCallback(
    async (shouldClearToken = false) => {
      if (shouldClearToken) {
        setToken("");
        setOneTimeToken("");
      }
      await authentication
        ?.getAuthToken()
        .then((newToken) => {
          setToken(newToken);
        })
        .catch(() => {
          setError(t("appDisabled"));
        });
    },
    [t, setToken, setOneTimeToken, setError]
  );

  const fetchOTT = useCallback(async () => {
    if (!oneTimeToken && token && subdomain && !isLoggedIn) {
      setStatus(t("status.tokenRequest"));
      await fetch(`${process.env.REACT_APP_API_BASEURL}/oauth/azure/ott`, {
        body: JSON.stringify({ token }),
        method: "POST",
        headers: { Subdomain: subdomain },
      })
        .then(async (res) => {
          const data = await res.json();
          return { ...data, status: res.status };
        })
        .then((data) => {
          if (data.status === 200) {
            setOneTimeToken(data.token);
          } else if (data.status === 401 || data.status === 403) {
            setError(t("401", { email, domain: subdomain }));
          } else if (data.status === 409) {
            setError(t("409", { email, domain: subdomain }));
          } else {
            setError(t("500"));
          }
          setStatus(data.status === 200 ? t("status.tokenRequestSuccess") : "");
        })
        .catch(() => setError(t("ottFailed")));
    }
  }, [t, token, subdomain, setOneTimeToken, setError, oneTimeToken, isLoggedIn, setStatus, email]);

  useEffect(() => {
    const listener = (e) => {
      getMessage(e, request, setIsLoggedIn);
    };
    window.addEventListener("message", listener);
    return () => {
      window.removeEventListener("message", listener);
    };
  }, [request, setIsLoggedIn]);

  useEffect(() => {
    request();
  }, [request]);

  useEffect(() => {
    fetchOTT();
  }, [fetchOTT]);
};
